import { Spin } from "antd";
import { useContext, useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Trans, useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { IImageReference } from "../../../../../../@types/Note";
import { PMPLogo } from "../../../../../../assets/icons/PMPLogo";
import { Button } from "../../../../../../components/Button";
import { ImageReferenceTooltip } from "../../../../../../components/ImageReferenceTooltip";
import { RetryButton } from "../../../../../../components/RetryButton";
import { RichTextEditor } from "../../../../../../components/RichTextEditor";
import { Tag } from "../../../../../../components/Tag";
import { noteTypes } from "../../../../../../constants/noteTypes";
import { ToastContext } from "../../../../../../contexts/ToastContext";
import { useGenerateAiSummary } from "../../../../../../hooks/mutations/notes";
import { getNoteData } from "../../../../../../hooks/queries/notes";
import { useModal } from "../../../../../../hooks/useModal";
import { noteService } from "../../../../../../services/noteService";
import { getLongFormattedDate } from "../../../../../../utils/dateFormatter";
import { renderNoteWithImages } from "../../../../../../utils/noteImageHandler";
import {
  AiSummaryCard,
  AiSummaryContent,
  AiSummaryHeader,
  AiSummarySection,
  AiSummaryTitle,
  AssignedToLabel,
  Container,
  EditButton,
  GenerateAiButton,
  GenerateAiButtonContainer,
  Label,
  LoadingContainer,
  LoadingMessage,
  NotAvailableContainer,
  NotAvailableMessage,
  SubmitContainer,
  TextAreaContainer,
  Title,
  UrgentChipContainer,
  spinCss,
} from "./ViewNoteModal.styles";

type ViewNoteModalProps = {
  noteId: number;
  onEditClick: (noteId: number) => void;
};

export function ViewNoteModal({ noteId, onEditClick }: ViewNoteModalProps) {
  const { t } = useTranslation();

  const [noteContent, setNoteContent] = useState("");
  const [renderedContent, setRenderedContent] = useState("");
  const [labelContent, setLabelContent] = useState("");
  const [isTransforming, setIsTransforming] = useState(false);
  const [images, setImages] = useState<IImageReference[]>([]);
  const [tooltip, setTooltip] = useState<{
    imageKey: string;
    displayOrder: number;
    position: { x: number; y: number };
  } | null>(null);
  const prevNotesRef = useRef<string>();
  const lastTransformedContentRef = useRef<string>();
  const { closeModal } = useModal();
  const { showToast } = useContext(ToastContext);
  const { generateAiSummary, isGenerating } = useGenerateAiSummary();

  const { patientNoteData, isFetching, isError, refetch, isRefetching } =
    getNoteData(noteId);

  const titleText =
    patientNoteData?.noteType === noteTypes.TASK
      ? t("patientPage_notesTable_viewTask")
      : t("patientPage_notesTable_viewNote");

  useEffect(() => {
    if (
      !isFetching &&
      !isError &&
      !isRefetching &&
      patientNoteData?.notes &&
      prevNotesRef.current !== patientNoteData.notes
    ) {
      prevNotesRef.current = patientNoteData.notes;
      setNoteContent(patientNoteData.notes);
      const timeStr = getLongFormattedDate(patientNoteData.time);
      const labelStr = `${timeStr} - ${patientNoteData.reviewedByEmail}`;
      setLabelContent(labelStr);
    }
  }, [
    patientNoteData?.notes,
    patientNoteData?.time,
    patientNoteData?.reviewedByEmail,
    isFetching,
    isError,
    isRefetching,
  ]);

  // Fetch images for tooltips
  useEffect(() => {
    const fetchImages = async () => {
      try {
        const fetchedImages = await noteService.getNoteImages(noteId);
        setImages(fetchedImages);
      } catch {
        // Silently fail — tooltips just won't show.
      }
    };
    fetchImages();
  }, [noteId]);

  // Transform note content to render S3 images with pre-signed URLs
  useEffect(() => {
    const transformContent = async () => {
      if (noteContent && lastTransformedContentRef.current !== noteContent) {
        lastTransformedContentRef.current = noteContent;
        setIsTransforming(true);
        const transformed = await renderNoteWithImages(noteContent, noteId);
        setRenderedContent(transformed);
        setIsTransforming(false);
      }
    };
    transformContent();
  }, [noteContent, noteId]);

  // Add event listeners for image reference tooltips
  useEffect(() => {
    const handleMouseEnter = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains("image-ref")) {
        const { imageKey, imageOrder: displayOrderStr } = target.dataset;

        if (imageKey && displayOrderStr) {
          const rect = target.getBoundingClientRect();
          setTooltip({
            imageKey,
            displayOrder: parseInt(displayOrderStr, 10),
            position: {
              x: rect.left + rect.width / 2,
              y: rect.bottom + 5,
            },
          });
        }
      }
    };

    const handleMouseLeave = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains("image-ref")) {
        setTooltip(null);
      }
    };

    const editorContainer = document.querySelector(".ql-editor");
    if (editorContainer) {
      editorContainer.addEventListener("mouseenter", handleMouseEnter, true);
      editorContainer.addEventListener("mouseleave", handleMouseLeave, true);
    }

    return () => {
      if (editorContainer) {
        editorContainer.removeEventListener(
          "mouseenter",
          handleMouseEnter,
          true
        );
        editorContainer.removeEventListener(
          "mouseleave",
          handleMouseLeave,
          true
        );
      }
    };
  }, [renderedContent]);

  // RichTextEditor requires onValueChange callback even in readOnly mode.
  const handleInputChange = () => {
    // No-op: read-only view.
  };

  const handleEditNoteClose = async () => {
    await closeModal();
    onEditClick(noteId);
  };

  const refreshData = () => {
    refetch();
  };

  const handleDoneClick = () => {
    closeModal();
  };

  const handleGenerateAiSummary = async () => {
    try {
      await generateAiSummary(noteId);
      showToast("AI Analysis generated successfully", "success");
    } catch (error: any) {
      showToast(
        error?.response?.data?.title ||
          error?.message ||
          "Failed to generate insights",
        "error"
      );
    }
  };

  if (isError) {
    return (
      <Container>
        <p>
          <Trans
            i18nKey="patientPage_note_loadError"
            components={{
              retry: <RetryButton onClick={refreshData} />,
            }}
          />
        </p>
      </Container>
    );
  }

  if (isFetching || isTransforming) {
    return (
      <Container>
        <Title>{titleText}</Title>
        <Label>{labelContent}</Label>
        <TextAreaContainer>
          <LoadingContainer>
            <Spin className={spinCss()} size="large" />
          </LoadingContainer>
        </TextAreaContainer>
        <SubmitContainer>
          <Button
            label={t("patientPage_notesTable_done")}
            type="button"
            onClick={handleDoneClick}
          />
          {patientNoteData?.editable && (
            <EditButton onClick={handleEditNoteClose}>
              {t("patientPage_notesTable_edit")}
            </EditButton>
          )}
        </SubmitContainer>
      </Container>
    );
  }

  // API interceptor swallows 4xx, so a missing note resolves as a successful
  // query with an error body. Detect it by the absent id rather than relying
  // on isError, then show a deep-link-friendly message.
  if (!patientNoteData?.id) {
    return (
      <NotAvailableContainer>
        <Title>{titleText}</Title>
        <NotAvailableMessage>
          {t("patientPage_notes_unavailable")}
        </NotAvailableMessage>
        <SubmitContainer>
          <Button
            label={t("patientPage_notesTable_done")}
            type="button"
            onClick={handleDoneClick}
          />
        </SubmitContainer>
      </NotAvailableContainer>
    );
  }

  const isTask = patientNoteData.noteType === noteTypes.TASK;
  const assignedHcp = patientNoteData.assignedHcpName ?? null;

  return (
    <Container>
      <Title>{titleText}</Title>
      <Label>{labelContent}</Label>
      {isTask && assignedHcp && (
        <AssignedToLabel>
          {t("patientPage_notes_assignedTo", { hcp: assignedHcp })}
        </AssignedToLabel>
      )}
      {isTask && patientNoteData.isUrgent && !patientNoteData.isCompleted && (
        <UrgentChipContainer>
          <Tag variant="error">{t("patientPage_notesTable_urgent")}</Tag>
        </UrgentChipContainer>
      )}
      <TextAreaContainer>
        <RichTextEditor
          key={`note-editor-${noteId}`}
          value={renderedContent}
          placeholderTxt={t("patientPage_notesTable_notePlaceholder")}
          onValueChange={handleInputChange}
          height={400}
          readOnly
        />
      </TextAreaContainer>

      {!patientNoteData?.aiSummary && !isGenerating && (
        <GenerateAiButtonContainer>
          <GenerateAiButton type="button" onClick={handleGenerateAiSummary}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 1L9.5 5.5L14 7L9.5 8.5L8 13L6.5 8.5L2 7L6.5 5.5L8 1Z"
                fill="currentColor"
              />
            </svg>
            Generate Insights
          </GenerateAiButton>
        </GenerateAiButtonContainer>
      )}

      {isGenerating && (
        <GenerateAiButtonContainer>
          <LoadingMessage>
            <Spin className={spinCss()} size="default" />
            Generating Insights...
          </LoadingMessage>
        </GenerateAiButtonContainer>
      )}

      {patientNoteData?.aiSummary && (
        <AiSummarySection>
          <AiSummaryCard>
            <AiSummaryHeader>
              <PMPLogo width="24" height="24" />
              <AiSummaryTitle>Insights by patientMpower</AiSummaryTitle>
            </AiSummaryHeader>
            <AiSummaryContent>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {patientNoteData.aiSummary}
              </ReactMarkdown>
            </AiSummaryContent>
          </AiSummaryCard>
        </AiSummarySection>
      )}

      <SubmitContainer>
        <Button
          label={t("patientPage_notesTable_done")}
          type="button"
          onClick={handleDoneClick}
        />
        {patientNoteData.editable && (
          <EditButton onClick={handleEditNoteClose}>
            {t("patientPage_notesTable_edit")}
          </EditButton>
        )}
      </SubmitContainer>

      {tooltip &&
        ReactDOM.createPortal(
          <ImageReferenceTooltip
            imageKey={tooltip.imageKey}
            displayOrder={tooltip.displayOrder}
            images={images}
            position={tooltip.position}
            onClose={() => setTooltip(null)}
          />,
          document.body
        )}
    </Container>
  );
}
